import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { dashboardAPI, alertsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
    Users,
    TrendingUp,
    Heart,
    AlertTriangle,
    RefreshCw,
    ArrowUpRight,
    ArrowDownRight,
    Minus
} from 'lucide-react';
import {
    AreaChart,
    Area,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell
} from 'recharts';
import { getScoreColor, getSeverityColor, formatDate } from '../../lib/utils';

const MetricCard = ({ title, value, subtitle, icon: Icon, trend, color, isCount }) => (
    <Card className="bento-card">
        <CardContent className="p-6">
            <div className="flex items-start justify-between">
                <div>
                    <p className="text-sm font-medium text-stone-500 mb-1">{title}</p>
                    <p className={`text-3xl font-bold font-heading ${color || 'text-stone-900'}`}>
                        {isCount ? value : (typeof value === 'number' ? (value * 100).toFixed(0) + '%' : value)}
                    </p>
                    {subtitle && <p className="text-xs text-stone-400 mt-1">{subtitle}</p>}
                </div>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color ? 'bg-indigo-50' : 'bg-stone-100'}`}>
                    <Icon className={`w-6 h-6 ${color || 'text-stone-600'}`} />
                </div>
            </div>
            {trend !== undefined && trend !== null && (
                <div className="flex items-center gap-1 mt-3">
                    {trend > 0 ? (
                        <ArrowUpRight className="w-4 h-4 text-emerald-500" />
                    ) : trend < 0 ? (
                        <ArrowDownRight className="w-4 h-4 text-red-500" />
                    ) : (
                        <Minus className="w-4 h-4 text-stone-400" />
                    )}
                    <span className={`text-xs font-medium ${trend > 0 ? 'text-emerald-500' : trend < 0 ? 'text-red-500' : 'text-stone-400'}`}>
                        {Math.abs(trend)}% from last week
                    </span>
                </div>
            )}
        </CardContent>
    </Card>
);

const HeatmapCell = ({ value, label }) => {
    const getColor = (v) => {
        if (v >= 0.7) return 'bg-emerald-500';
        if (v >= 0.5) return 'bg-amber-400';
        if (v >= 0.3) return 'bg-orange-500';
        return 'bg-red-500';
    };

    return (
        <div className="flex flex-col items-center gap-1">
            <div
                className={`w-full aspect-square rounded-lg ${getColor(value)} flex items-center justify-center text-white font-mono text-sm font-medium`}
            >
                {(value * 100).toFixed(0)}
            </div>
            <span className="text-xs text-stone-500 truncate max-w-full">{label}</span>
        </div>
    );
};

export default function HRDashboard() {
    const [metrics, setMetrics] = useState(null);
    const [heatmapData, setHeatmapData] = useState(null);
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generatingAlerts, setGeneratingAlerts] = useState(false);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [metricsRes, heatmapRes, alertsRes] = await Promise.all([
                dashboardAPI.getMetrics(),
                dashboardAPI.getHeatmap(),
                alertsAPI.getAll().catch(() => ({ data: [] }))
            ]);
            setMetrics(metricsRes.data);
            setHeatmapData(heatmapRes.data);
            setAlerts(alertsRes.data || []);
        } catch (error) {
            console.error('Failed to fetch dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleGenerateAlerts = async () => {
        try {
            setGeneratingAlerts(true);
            await alertsAPI.generate();
            const alertsRes = await alertsAPI.getAll();
            setAlerts(alertsRes.data || []);
        } catch (error) {
            console.error('Failed to generate alerts:', error);
        } finally {
            setGeneratingAlerts(false);
        }
    };

    if (loading) {
        return (
            <div className="p-8 flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-stone-500">Loading dashboard...</p>
                </div>
            </div>
        );
    }

    const chartColors = ['#4F46E5', '#10B981', '#F47266', '#F59E0B'];

    return (
        <div className="p-8" data-testid="hr-dashboard">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-3xl font-bold font-heading text-stone-900">HR Dashboard</h1>
                        <p className="text-stone-500 mt-1">Workforce intelligence at a glance</p>
                    </div>
                    <Button onClick={fetchData} variant="outline" className="gap-2" data-testid="refresh-btn">
                        <RefreshCw className="w-4 h-4" />
                        Refresh
                    </Button>
                </div>

                {/* Metrics Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <MetricCard
                        title="Total Employees"
                        value={metrics?.total_employees || 0}
                        subtitle="Active workforce"
                        icon={Users}
                        isCount={true}
                    />
                    <MetricCard
                        title="Sentiment Score"
                        value={metrics?.avg_sentiment_score || 0}
                        subtitle="Company average"
                        icon={TrendingUp}
                        color="text-indigo-600"
                    />
                    <MetricCard
                        title="Engagement"
                        value={metrics?.avg_engagement_score || 0}
                        subtitle="Participation rate"
                        icon={Heart}
                        color="text-emerald-600"
                    />
                    <MetricCard
                        title="At-Risk Employees"
                        value={metrics?.high_risk_count || 0}
                        subtitle="Requiring attention"
                        icon={AlertTriangle}
                        color="text-coral-DEFAULT"
                        isCount={true}
                    />
                </div>

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                    {/* Sentiment Trend Chart */}
                    <Card className="lg:col-span-2 bento-card">
                        <CardHeader>
                            <CardTitle className="font-heading">Sentiment Trend</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="h-64">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={metrics?.sentiment_trend || []}>
                                        <defs>
                                            <linearGradient id="sentimentGradient" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.3} />
                                                <stop offset="95%" stopColor="#4F46E5" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                                        <XAxis
                                            dataKey="date"
                                            tick={{ fontSize: 12, fill: '#78716c' }}
                                            tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                        />
                                        <YAxis
                                            domain={[0, 1]}
                                            tick={{ fontSize: 12, fill: '#78716c' }}
                                            tickFormatter={(val) => `${(val * 100).toFixed(0)}%`}
                                        />
                                        <Tooltip
                                            content={({ active, payload }) => {
                                                if (active && payload?.[0]) {
                                                    return (
                                                        <div className="glass p-3 rounded-xl">
                                                            <p className="text-sm font-medium text-stone-900">
                                                                {(payload[0].value * 100).toFixed(0)}%
                                                            </p>
                                                            <p className="text-xs text-stone-500">
                                                                {formatDate(payload[0].payload.date)}
                                                            </p>
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            }}
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey="score"
                                            stroke="#4F46E5"
                                            strokeWidth={2}
                                            fill="url(#sentimentGradient)"
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Alerts Panel */}
                    <Card className="bento-card">
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle className="font-heading">Recent Alerts</CardTitle>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={handleGenerateAlerts}
                                disabled={generatingAlerts}
                                data-testid="generate-alerts-btn"
                            >
                                {generatingAlerts ? 'Generating...' : 'Analyze'}
                            </Button>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-3 max-h-64 overflow-y-auto">
                                {alerts.length === 0 ? (
                                    <p className="text-sm text-stone-500 text-center py-8">
                                        No alerts yet. Click "Analyze" to generate.
                                    </p>
                                ) : (
                                    alerts.slice(0, 5).map((alert) => (
                                        <div
                                            key={alert.id}
                                            className={`p-3 rounded-xl border ${getSeverityColor(alert.severity)}`}
                                        >
                                            <div className="flex items-start justify-between gap-2">
                                                <div>
                                                    <p className="text-sm font-medium">{alert.employee_name}</p>
                                                    <p className="text-xs opacity-80 mt-0.5">{alert.alert_type.replace('_', ' ')}</p>
                                                </div>
                                                <Badge variant="outline" className="capitalize text-xs">
                                                    {alert.severity}
                                                </Badge>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Heatmap & Department Stats */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Department Heatmap */}
                    <Card className="bento-card">
                        <CardHeader>
                            <CardTitle className="font-heading">Culture Heatmap</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <Tabs defaultValue="sentiment">
                                <TabsList className="mb-4">
                                    <TabsTrigger value="sentiment">Sentiment</TabsTrigger>
                                    <TabsTrigger value="engagement">Engagement</TabsTrigger>
                                    <TabsTrigger value="wellbeing">Well-being</TabsTrigger>
                                </TabsList>
                                <TabsContent value="sentiment">
                                    <div className="grid grid-cols-4 gap-3">
                                        {(heatmapData?.heatmap || []).map((dept, i) => (
                                            <HeatmapCell
                                                key={dept.department}
                                                value={dept.sentiment}
                                                label={dept.department}
                                            />
                                        ))}
                                        {(!heatmapData?.heatmap || heatmapData.heatmap.length === 0) && (
                                            <p className="col-span-4 text-sm text-stone-500 text-center py-8">
                                                No department data available yet.
                                            </p>
                                        )}
                                    </div>
                                </TabsContent>
                                <TabsContent value="engagement">
                                    <div className="grid grid-cols-4 gap-3">
                                        {(heatmapData?.heatmap || []).map((dept, i) => (
                                            <HeatmapCell
                                                key={dept.department}
                                                value={dept.engagement}
                                                label={dept.department}
                                            />
                                        ))}
                                    </div>
                                </TabsContent>
                                <TabsContent value="wellbeing">
                                    <div className="grid grid-cols-4 gap-3">
                                        {(heatmapData?.heatmap || []).map((dept, i) => (
                                            <HeatmapCell
                                                key={dept.department}
                                                value={dept.wellbeing}
                                                label={dept.department}
                                            />
                                        ))}
                                    </div>
                                </TabsContent>
                            </Tabs>
                        </CardContent>
                    </Card>

                    {/* Department Comparison */}
                    <Card className="bento-card">
                        <CardHeader>
                            <CardTitle className="font-heading">Department Performance</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="h-64">
                                {heatmapData?.heatmap && heatmapData.heatmap.length > 0 ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart
                                            data={heatmapData.heatmap}
                                            layout="vertical"
                                            margin={{ left: 80 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                                            <XAxis
                                                type="number"
                                                domain={[0, 1]}
                                                tickFormatter={(val) => `${(val * 100).toFixed(0)}%`}
                                                tick={{ fontSize: 12, fill: '#78716c' }}
                                            />
                                            <YAxis
                                                dataKey="department"
                                                type="category"
                                                tick={{ fontSize: 12, fill: '#78716c' }}
                                            />
                                            <Tooltip
                                                content={({ active, payload }) => {
                                                    if (active && payload?.[0]) {
                                                        return (
                                                            <div className="glass p-3 rounded-xl">
                                                                <p className="text-sm font-medium text-stone-900">
                                                                    {(payload[0].value * 100).toFixed(0)}% Sentiment
                                                                </p>
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                }}
                                            />
                                            <Bar dataKey="sentiment" radius={[0, 4, 4, 0]}>
                                                {heatmapData.heatmap.map((entry, index) => (
                                                    <Cell
                                                        key={entry.department}
                                                        fill={entry.sentiment >= 0.7 ? '#10B981' : entry.sentiment >= 0.5 ? '#F59E0B' : '#F47266'}
                                                    />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <div className="flex items-center justify-center h-full">
                                        <p className="text-sm text-stone-500">No department data available yet.</p>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </motion.div>
        </div>
    );
}
